import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db90l51rh5tg6sock200";

const feedbackTypes = ["内容建议", "合作洽谈", "内容纠错", "其他事项"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const okCode = parsed?.code === "OK";

      if (response.ok && okCode && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-background-50 rounded-lg border border-background-200 p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
          <i className="ri-checkbox-circle-line text-3xl" />
        </div>
        <h3 className="mt-5 font-heading text-xl font-bold text-foreground-900">感谢你的反馈</h3>
        <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
          我们已经收到你的留言，会认真阅读每一条建议。感谢你与我们一起，让内容变得更好。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-700 mb-2">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-700 mb-2">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-type" className="block text-sm font-medium text-foreground-700 mb-2">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="type"
          defaultValue={feedbackTypes[0]}
          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
        >
          {feedbackTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-700 mb-2">
          你的留言
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="告诉我们你的想法、建议或合作意向（最多 500 字）"
          className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors placeholder:text-foreground-400 resize-none"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="field-alt-verify"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError && (
        <p className="mt-5 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-4 py-3">
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}